import { createContext } from 'react';
import {
	TFileId,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	IServerError,
	ICreateDishValidationError,
	TDishValidationType,
	TLanguageIDs,
	IMenuTranslation,
	IDefaultLanguage,
	IDishAllSubCategory,
	ITotalPrice,
	IDishModifierGroup,
	IItemForGetShortDishInfo,
	IRestaurantCategory,
} from 'types';

export type TEditDishVisibleForm = 'mainForm' | 'translations';
export type TTypeOfEditDishModalWindow =
	| null
	| 'toConfirmFieldsClearing'
	| 'success'
	| 'error'
	| 'defaultTranslationIsEmpty'
	| 'comboModifierValidationError';
export interface IEditDishErrorResponseData extends IServerError<ICreateDishValidationError> {
	errorType: TDishValidationType;
}
export interface IIDsShowInComboDishes {
	[key: string]: boolean; // key - is the ID and boolean means showInCombo or hiddenFromCombo status.
}
export interface IModifierGroupWithAdditionalData {
	modifierGroupId: string;
	lowerLimit: string;
	upperLimit: string;
	isLimitsProtectedFromSync: boolean;
}

export interface IInitialFormData {
	name: string;
	selectedCustomer: Nullable<string>;
	totalPrices: ITotalPrice[];
	restaurantWithTurnOffComment: string[];
	restaurantWithRequiredComment: string[];
	shortDescription: string;
	posName: string | null;
	selectedComboModifier: Nullable<string>;
	externalId: string | null;
	isDisabledInRestaurants: string[];
	activationSchedule: string;
	deactivationSchedule: string;
	selectedModifierGroups: string[];
	modifierGroupsWithAdditionalData: IModifierGroupWithAdditionalData[];
	selectedModifierGroupsAsAdditional: string[];
	selectedSuggestedDishes: string[];
	selectedAdditionalDishes: string[];
	selectedDishModifications: string[];
	IDsShowInComboDishes: IIDsShowInComboDishes;
	filePath: Nullable<string>;
	smallFilePath: Nullable<string>;
	videoPath: Nullable<string>;
	translations: IMenuTranslation[];
	isTaxPercentProtectedFromSync: boolean;
	taxPercent: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	isFiscalized: boolean;
}

export interface IEditDishState {
	needRefetchData: boolean;
	initialFormData: IInitialFormData | null;
	visibleForm: TEditDishVisibleForm;
	dishId: string;
	filePath: Nullable<string>;
	preSignedUrl: string | null;
	fileId: TFileId;
	smallFilePath: Nullable<string>;
	smallPreSignedUrl: string | null;
	smallFileId: TFileId;
	videoPath: Nullable<string>;
	videoPreSignedUrl: string | null;
	videoFileId: TFileId;
	isVideoActive: boolean;
	name: string;
	selectedCustomer: Nullable<string>;
	articleNumber: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	restaurantsForPrice: string[];
	price: string;
	totalPrices: ITotalPrice[];
	restaurantWithTurnOffComment: string[];
	restaurantWithRequiredComment: string[];
	shortDescription: string;
	posName: string | null;
	isComboDish: boolean;
	fetchedComboModifier: IItemForGetShortInfo[];
	initialSelectedComboModifier: Nullable<string>;
	selectedComboModifier: Nullable<string>;
	initialCategories: IRestaurantCategory[];
	initialSubCategories: IDishAllSubCategory[];
	externalId: string | null;
	isCreatedInPos: boolean;
	isDisabledInRestaurants: string[];
	defaultCustomerLanguage: Nullable<IDefaultLanguage>;
	selectedTranslationTab: TLanguageIDs | null;
	translations: IMenuTranslation[];
	activationSchedule: string;
	deactivationSchedule: string;
	fetchedModifierGroups: IItemForGetShortInfo[];
	initialModifierGroups: IDishModifierGroup[];
	selectedModifierGroups: string[];
	modifierGroupsWithAdditionalData: IModifierGroupWithAdditionalData[];
	initialModifierGroupsAsAdditional: string[];
	selectedModifierGroupsAsAdditional: string[];
	fetchedCategories: IItemForGetShortInfo[];
	selectedSuggestedDishCategory: Nullable<string>;
	fetchedSuggestedDishSubCategories: IItemForGetShortInfo[];
	selectedSuggestedDishSubCategory: Nullable<string>;
	fetchedSuggestedDishes: IItemForGetShortDishInfo[];
	selectedSuggestedDishes: string[];
	selectedAdditionalDishCategory: Nullable<string>;
	fetchedAdditionalDishSubCategories: IItemForGetShortInfo[];
	selectedAdditionalDishSubCategory: Nullable<string>;
	fetchedAdditionalDishes: IItemForGetShortDishInfo[];
	selectedAdditionalDishes: string[];
	selectedDishModificationCategory: Nullable<string>;
	fetchedDishModificationSubCategories: IItemForGetShortInfo[];
	selectedDishModificationSubCategory: Nullable<string>;
	fetchedDishModifications: IItemForGetShortDishInfo[];
	selectedDishModifications: string[];
	IDsShowInComboDishes: IIDsShowInComboDishes;
	isTaxPercentProtectedFromSync: boolean;
	taxPercent: Nullable<number>;
	hasOpenPrice: boolean;
	openPrice: Nullable<number>;
	openPriceMaxThreshold: Nullable<number>;
	isFiscalized: boolean;
	typeOfModalWindow: TTypeOfEditDishModalWindow;
	errorResponseData: IEditDishErrorResponseData | null;
}

export const editDishInitialState: IEditDishState = {
	needRefetchData: false,
	initialFormData: null,
	visibleForm: 'mainForm',
	dishId: '',
	filePath: null,
	preSignedUrl: null,
	fileId: null,
	smallFilePath: null,
	smallPreSignedUrl: null,
	smallFileId: null,
	videoPath: null,
	videoPreSignedUrl: null,
	videoFileId: null,
	isVideoActive: false,
	name: '',
	selectedCustomer: null,
	articleNumber: null,
	fetchedRestaurants: [],
	restaurantsForPrice: [],
	price: '',
	totalPrices: [],
	restaurantWithTurnOffComment: [],
	restaurantWithRequiredComment: [],
	shortDescription: '',
	posName: null,
	isComboDish: false,
	fetchedComboModifier: [],
	initialSelectedComboModifier: null,
	selectedComboModifier: null,
	initialCategories: [],
	initialSubCategories: [],
	externalId: '',
	isCreatedInPos: false,
	isDisabledInRestaurants: [],
	defaultCustomerLanguage: null,
	selectedTranslationTab: null,
	translations: [],
	activationSchedule: '',
	deactivationSchedule: '',
	fetchedModifierGroups: [],
	initialModifierGroups: [],
	selectedModifierGroups: [],
	modifierGroupsWithAdditionalData: [],
	initialModifierGroupsAsAdditional: [],
	selectedModifierGroupsAsAdditional: [],
	fetchedCategories: [],
	selectedSuggestedDishCategory: null,
	fetchedSuggestedDishSubCategories: [],
	selectedSuggestedDishSubCategory: null,
	fetchedSuggestedDishes: [],
	selectedSuggestedDishes: [],
	selectedAdditionalDishCategory: null,
	fetchedAdditionalDishSubCategories: [],
	selectedAdditionalDishSubCategory: null,
	fetchedAdditionalDishes: [],
	selectedAdditionalDishes: [],
	selectedDishModificationCategory: null,
	fetchedDishModificationSubCategories: [],
	selectedDishModificationSubCategory: null,
	fetchedDishModifications: [],
	selectedDishModifications: [],
	IDsShowInComboDishes: {},
	isTaxPercentProtectedFromSync: false,
	taxPercent: null,
	hasOpenPrice: false,
	openPrice: null,
	openPriceMaxThreshold: null,
	isFiscalized: false,
	typeOfModalWindow: null,
	errorResponseData: null,
};

export interface IEditDishContext extends IEditDishState {
	refetchData(value: boolean): void;
	changeVisibleForm(value: TEditDishVisibleForm): void;
	setFileId(value: TFileId): void;
	deletePhoto(): void;
	setSmallFileId(value: TFileId): void;
	deleteSmallPhoto(): void;
	setVideoFileId(value: TFileId): void;
	deleteVideo(): void;
	setIsVideoActive(value: boolean): void;
	changeDishName(value: string): void;
	setFetchedRestaurants(value: IItemForGetShortInfo[]): void;
	setRestaurantsForPrice(value: string[]): void;
	changePrice(value: string): void;
	addPrices(value: ITotalPrice[]): void;
	removePrice(value: string[]): void;
	setRestaurantWithTurnOffComment(value: string[]): void;
	setRestaurantWithRequiredComment(value: string[]): void;
	changeShortDescription(value: string): void;
	setFetchedComboModifier(value: IItemForGetShortInfo[]): void;
	setComboModifier(value: Nullable<string>): void;
	updateComboModifier(value: IItemForGetShortInfo[]): void;
	setDisabledInRestaurants(value: string[]): void;
	setDefaultCustomerLanguage(value: Nullable<IDefaultLanguage>): void;
	setTranslationTab(value: TLanguageIDs | null): void;
	changeNameTranslation(languageId: TLanguageIDs, value: string): void;
	changeDescriptionTranslation(languageId: TLanguageIDs, value: string): void;
	changeActivationSchedule(value: string): void;
	changeDeactivationSchedule(value: string): void;
	setFetchedModifierGroups(value: IItemForGetShortInfo[]): void;
	setModifierGroups(value: string[]): void;
	changeLowerLimit(id: string, value: string): void;
	changeUpperLimit(id: string, value: string): void;
	toggleLimitsProtectedFromSync(id: string, value: boolean): void;
	setModifierGroupsAsAdditional(value: string[]): void;
	setFetchedCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishSubCategories(value: IItemForGetShortInfo[]): void;
	setSuggestedDishSubCategory(value: Nullable<string>): void;
	setFetchedSuggestedDishes(value: IItemForGetShortInfo[]): void;
	setSuggestedDishes(value: string[]): void;
	removeSuggestedDish(value: string): void;
	setAdditionalDishCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishSubCategories(value: IItemForGetShortInfo[]): void;
	setAdditionalDishSubCategory(value: Nullable<string>): void;
	setFetchedAdditionalDishes(value: IItemForGetShortInfo[]): void;
	setAdditionalDishes(value: string[]): void;
	removeAdditionalDish(value: string): void;
	setDishModificationCategory(value: Nullable<string>): void;
	setFetchedDishModificationSubCategories(value: IItemForGetShortInfo[]): void;
	setDishModificationSubCategory(value: Nullable<string>): void;
	setFetchedDishModifications(value: IItemForGetShortInfo[]): void;
	setDishModifications(value: string[]): void;
	removeDishModification(value: string): void;
	setIDsShowInComboDishes(value: IIDsShowInComboDishes): void;
	changeExternalId(value: string): void;
	generateUniqueExternalId(): void;
	toggleTaxPercentProtectedFromSync(value: boolean): void;
	changeTaxPercent(value: Nullable<number>): void;
	toggleOpenPrice(value: boolean): void;
	changeOpenPrice(value: Nullable<number>): void;
	changeOpenPriceMaxThreshold(value: Nullable<number>): void;
	toggleFiscalized(): void;
	changeTypeOfModalWindow(value: TTypeOfEditDishModalWindow): void;
	showErrorModalWithInfo(value: IEditDishErrorResponseData | null): void;
	resetFields(): void;
}

export const EditDishContext = createContext<IEditDishContext>({
	refetchData: () => undefined,
	changeVisibleForm: () => undefined,
	setFileId: () => undefined,
	deletePhoto: () => undefined,
	setSmallFileId: () => undefined,
	deleteSmallPhoto: () => undefined,
	setVideoFileId: () => undefined,
	deleteVideo: () => undefined,
	setIsVideoActive: () => undefined,
	changeDishName: () => undefined,
	setFetchedRestaurants: () => undefined,
	setRestaurantsForPrice: () => undefined,
	changePrice: () => undefined,
	addPrices: () => undefined,
	removePrice: () => undefined,
	setRestaurantWithTurnOffComment: () => undefined,
	setRestaurantWithRequiredComment: () => undefined,
	changeShortDescription: () => undefined,
	setFetchedComboModifier: () => undefined,
	setComboModifier: () => undefined,
	updateComboModifier: () => undefined,
	setDisabledInRestaurants: () => undefined,
	setDefaultCustomerLanguage: () => undefined,
	setTranslationTab: () => undefined,
	changeNameTranslation: () => undefined,
	changeDescriptionTranslation: () => undefined,
	changeActivationSchedule: () => undefined,
	changeDeactivationSchedule: () => undefined,
	setFetchedModifierGroups: () => undefined,
	setModifierGroups: () => undefined,
	changeLowerLimit: () => undefined,
	changeUpperLimit: () => undefined,
	toggleLimitsProtectedFromSync: () => undefined,
	setModifierGroupsAsAdditional: () => undefined,
	setFetchedCategories: () => undefined,
	setSuggestedDishCategory: () => undefined,
	setFetchedSuggestedDishSubCategories: () => undefined,
	setSuggestedDishSubCategory: () => undefined,
	setFetchedSuggestedDishes: () => undefined,
	setSuggestedDishes: () => undefined,
	removeSuggestedDish: () => undefined,
	setAdditionalDishCategory: () => undefined,
	setFetchedAdditionalDishSubCategories: () => undefined,
	setAdditionalDishSubCategory: () => undefined,
	setFetchedAdditionalDishes: () => undefined,
	setAdditionalDishes: () => undefined,
	removeAdditionalDish: () => undefined,
	setDishModificationCategory: () => undefined,
	setFetchedDishModificationSubCategories: () => undefined,
	setDishModificationSubCategory: () => undefined,
	setFetchedDishModifications: () => undefined,
	setDishModifications: () => undefined,
	removeDishModification: () => undefined,
	setIDsShowInComboDishes: () => undefined,
	changeExternalId: () => undefined,
	generateUniqueExternalId: () => undefined,
	toggleTaxPercentProtectedFromSync: () => undefined,
	changeTaxPercent: () => undefined,
	toggleOpenPrice: () => undefined,
	changeOpenPrice: () => undefined,
	changeOpenPriceMaxThreshold: () => undefined,
	toggleFiscalized: () => undefined,
	changeTypeOfModalWindow: () => undefined,
	showErrorModalWithInfo: () => undefined,
	resetFields: () => undefined,
	...editDishInitialState,
});
